import {
  Box,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  SxProps,
  Typography
} from '@mui/material';
import { BarChart, BarSeriesType, cheerfulFiestaPalette } from '@mui/x-charts';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useDashboardController } from 'api/controllers/DashboardController';

import { useTranslations } from 'context/TranslationContext';

import { useCurrency } from 'hooks/useCurrency';
import { useYearSelector } from 'hooks/useYearSelector';

import {
  CHARTS_LABEL_MAX_CHARS,
  CHARTS_LABEL_MAX_CHARS_WITHOUT_ELIPSIS
} from 'utils/constants/constants';
import { formatMoneyWithCurrency } from 'utils/helpers/moneyHelper';
import { mapHighlightToItems } from 'utils/mappers/charts';

import { chartTitle, yearSelector } from 'styles/components/Charts';

interface BudgetVsActualProps {
  sx?: SxProps;
}

export const BudgetVsActual = ({ sx }: BudgetVsActualProps) => {
  const { getBudgetByCompany } = useDashboardController();
  const { translate } = useTranslations();
  const { companyId } = useParams();
  const { selectedYear, setExistingYears, availableYears, setSelectedYear } =
    useYearSelector(false);
  const { getCurrentCompanyCurrency } = useCurrency();

  const [budgetsData, setBudgetsData] = useState(null);

  const fetchBudgets = useCallback(async () => {
    const response = await getBudgetByCompany(Number(companyId), selectedYear);

    setBudgetsData(response.budgets || []);
    setExistingYears(response.years || []);
  }, [companyId, selectedYear, getBudgetByCompany]);

  const handleYearChange = (event: SelectChangeEvent<number>) => {
    setSelectedYear(event.target.value as number);
  };

  useEffect(() => {
    fetchBudgets();
  }, [fetchBudgets]);

  return (
    <Paper elevation={4} sx={sx}>
      <Stack height="100%">
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Stack justifyContent="center">
            <Typography sx={chartTitle} align="left">
              {translate('labels.budgetVsActualAsOfToday')}
            </Typography>
          </Stack>
          <Stack justifyContent="center" sx={{ mr: 2, mt: 2 }}>
            <Select
              value={selectedYear}
              size="small"
              sx={yearSelector}
              onChange={handleYearChange}
            >
              {availableYears.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </Stack>
        </Box>
        <Box height="90%">
          <BarChart
            colors={cheerfulFiestaPalette}
            loading={!budgetsData}
            dataset={budgetsData || []}
            slotProps={{
              legend: {
                itemGap: 25,
                markGap: 3,
                itemMarkWidth: 10,
                itemMarkHeight: 10,
                labelStyle: {
                  fontSize: 12
                }
              },
              loadingOverlay: {
                message: translate('labels.loading')
              },
              noDataOverlay: {
                message: translate('labels.noData')
              }
            }}
            margin={{ left: 80, bottom: 90 }}
            series={
              mapHighlightToItems([
                {
                  dataKey: 'budget',
                  label: translate('labels.budget'),
                  valueFormatter: (value: number) =>
                    formatMoneyWithCurrency(value, getCurrentCompanyCurrency())
                },
                {
                  dataKey: 'actual',
                  label: translate('labels.actual'),
                  valueFormatter: (value: number) =>
                    formatMoneyWithCurrency(value, getCurrentCompanyCurrency())
                }
              ]) as unknown as BarSeriesType[]
            }
            xAxis={[
              {
                scaleType: 'band',
                dataKey: 'label',
                valueFormatter: (value: string, context: any) =>
                  context.location === 'tick' &&
                  value.length > CHARTS_LABEL_MAX_CHARS_WITHOUT_ELIPSIS
                    ? `${value.slice(0, CHARTS_LABEL_MAX_CHARS)}...`
                    : value,
                tickLabelStyle: {
                  angle: -45,
                  dominantBaseline: 'auto',
                  textAnchor: 'end',
                  fontSize: 10
                }
              }
            ]}
            yAxis={[
              {
                tickLabelStyle: {
                  fontSize: 10
                }
              }
            ]}
          />
        </Box>
      </Stack>
    </Paper>
  );
};
